import { useEffect, useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import airflowVideo from "../assets/airflow-loop.mp4";

export default function App() {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (prefersReducedMotion) {
      video.pause();
      return;
    }
    const play = video.play();
    if (play && typeof play.catch === "function") {
      play.catch(() => {
        /* autoplay blocked - ignore */
      });
    }
  }, [prefersReducedMotion]);

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  return (
    <section ref={rootRef} className="af">
      <div className="af-inner">
        <div className="af-header">
          <motion.span className="af-eyebrow" {...fade(0)}>
            <span className="af-eyebrow-mark" />
            Chassis Engineering
          </motion.span>

          <motion.h2 className="af-title" {...fade(0.1)}>
            AI Workstation Chassis Built for{" "}
            <span className="af-title-accent">Multi-GPU Cooling</span>
          </motion.h2>
        </div>

        <motion.div
          className="af-stage"
          initial={{ opacity: 0, scale: 0.985 }}
          animate={
            shouldAnimate ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1 }
          }
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="af-video-frame">
            <video
              ref={videoRef}
              className="af-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              aria-hidden="true"
              tabIndex={-1}
            >
              <source src={airflowVideo} type="video/mp4" />
            </video>
          </div>

          <motion.div className="af-body-wrap" {...fade(0.35)}>
            <p className="af-body af-body-lead">
              Every ArsenalPC AI workstation pairs a high-airflow chassis with
              multi-GPU thermals: full ATX support for dual RTX Pro 6000s,
              custom liquid cooling, and tool-free NVMe bays.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
